import Hotkeys from 'react-hot-keys';
import { motion } from 'framer-motion';
import { track } from '../../tracking';

const ValidatedBankDetails: React.FC<{
    validatedBankDetails: { account_number?: string; upi_id?: string; registered_name: string };
    onClose: () => void;
    onConfirm: () => void;
    isSavingProfile?: boolean;
}> = ({ validatedBankDetails, onClose, onConfirm, isSavingProfile }) => {
    const { registered_name, account_number, upi_id } = validatedBankDetails;

    const onConfirmClick = () => {
        onConfirm();
        track('CONFIRM_BANK_DETAILS', { registered_name, account_number, upi_id });
    };
    const onCloseClick = () => {
        onClose();
        track('CHANGE_BANK_DETAILS', { registered_name, account_number, upi_id });
    };
    return (
        <Hotkeys keyName="esc">
            <motion.div
                initial={{ opacity: 0 }}
                exit={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{ zIndex: 20 }}
                transition={{ duration: 0.2, ease: 'linear' }}
                className="font-lato select-none"
            >
                <div className="modal-overlay absolute inset-0 bg-darwin-neutral-1000 bg-opacity-50 z-50"></div>
                <div
                    className="modal-content space-y-16"
                    style={{
                        position: 'absolute',
                        width: '350px',
                        background: 'transparent',
                    }}
                >
                    <div className="px-16 py-24 bg-white rounded-4 space-y-16">
                        <div className="relative">
                            <p className="text-center font-700 text-16">Please Review Details</p>
                            <button
                                disabled={isSavingProfile}
                                onClick={onCloseClick}
                                className={`absolute top-5 right-0 ${
                                    isSavingProfile ? 'opacity-70' : ''
                                }`}
                            >
                                <svg
                                    className="w-14 h-14 fill-text-secondary"
                                    viewBox="0 0 12 12"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path d="M10.75 9.71875C11.0312 10.0312 11.0312 10.5 10.75 10.7812C10.4375 11.0938 9.96875 11.0938 9.6875 10.7812L6 7.0625L2.28125 10.7812C1.96875 11.0938 1.5 11.0938 1.21875 10.7812C0.90625 10.5 0.90625 10.0312 1.21875 9.71875L4.9375 6L1.21875 2.28125C0.90625 1.96875 0.90625 1.5 1.21875 1.21875C1.5 0.90625 1.96875 0.90625 2.25 1.21875L6 4.96875L9.71875 1.25C10 0.9375 10.4688 0.9375 10.75 1.25C11.0625 1.53125 11.0625 2 10.75 2.3125L7.03125 6L10.75 9.71875Z" />
                                </svg>
                            </button>
                        </div>

                        <div className="text-center space-y-8">
                            <p>
                                <span className="font-700">“{account_number || upi_id}” </span>
                                belongs to
                            </p>
                            <p className="text-18 font-700">{registered_name}</p>
                        </div>

                        <div className="flex items-start justify-center space-x-8">
                            <div className="relative top-3">
                                <svg
                                    className="w-14 h-14 fill-darwin-neutral-600"
                                    viewBox="0 0 14 14"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path d="M7.4 13.625C3.79453 13.625 0.9 10.7305 0.9 7.125C0.9 3.54492 3.79453 0.625 7.4 0.625C10.9801 0.625 13.9 3.54492 13.9 7.125C13.9 10.7305 10.9801 13.625 7.4 13.625ZM7.4 3.875C7.04453 3.875 6.79062 4.1543 6.79062 4.48438V7.32812C6.79062 7.68359 7.04453 7.9375 7.4 7.9375C7.73008 7.9375 8.00937 7.68359 8.00937 7.32812V4.48438C8.00937 4.1543 7.73008 3.875 7.4 3.875ZM8.2125 9.5625C8.2125 9.13086 7.83164 8.75 7.4 8.75C6.94297 8.75 6.5875 9.13086 6.5875 9.5625C6.5875 10.0195 6.94297 10.375 7.4 10.375C7.83164 10.375 8.2125 10.0195 8.2125 9.5625Z" />
                                </svg>
                            </div>

                            <p className="text-13 text-darwin-neutral-600">
                                Confirm your {account_number ? 'bank account' : 'upi id'} details
                                before proceeding. completed settlements are irreversible.
                            </p>
                        </div>

                        <div className="grid grid-cols-2 space-x-16 pt-12">
                            <button
                                className={`p-8 border border-darwin-accent-bright-blue-800 text-darwin-accent-bright-blue-800 rounded-4 font-700 ${
                                    isSavingProfile ? 'opacity-70' : ''
                                }`}
                                onClick={onCloseClick}
                                disabled={isSavingProfile}
                            >
                                Change
                            </button>
                            <button
                                className={`flex items-center justify-center space-x-10 p-8 text-white bg-darwin-accent-bright-blue-800 rounded-4 font-700 ${
                                    isSavingProfile ? 'opacity-70' : ''
                                }`}
                                onClick={onConfirmClick}
                                disabled={isSavingProfile}
                            >
                                {isSavingProfile && (
                                    <svg
                                        className="w-14 h-14 fill-white animate-spin"
                                        role="img"
                                        xmlns="http://www.w3.org/2000/svg"
                                        viewBox="0 0 512 512"
                                    >
                                        <path d="M96 256c0-26.5-21.5-48-48-48S0 229.5 0 256s21.5 48 48 48S96 282.5 96 256zM108.9 60.89c-26.5 0-48.01 21.49-48.01 47.99S82.39 156.9 108.9 156.9s47.99-21.51 47.99-48.01S135.4 60.89 108.9 60.89zM108.9 355.1c-26.5 0-48.01 21.51-48.01 48.01S82.39 451.1 108.9 451.1s47.99-21.49 47.99-47.99S135.4 355.1 108.9 355.1zM256 416c-26.5 0-48 21.5-48 48S229.5 512 256 512s48-21.5 48-48S282.5 416 256 416zM464 208C437.5 208 416 229.5 416 256s21.5 48 48 48S512 282.5 512 256S490.5 208 464 208zM403.1 355.1c-26.5 0-47.99 21.51-47.99 48.01S376.6 451.1 403.1 451.1s48.01-21.49 48.01-47.99S429.6 355.1 403.1 355.1zM256 0C229.5 0 208 21.5 208 48S229.5 96 256 96s48-21.5 48-48S282.5 0 256 0z"></path>
                                    </svg>
                                )}
                                <p>Confirm</p>
                            </button>
                        </div>
                    </div>
                </div>
            </motion.div>
        </Hotkeys>
    );
};

export default ValidatedBankDetails;
